import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { translations } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("appomax_lang") || "es");
  const [theme, setTheme] = useState(() => localStorage.getItem("appomax_theme") || "light");
  const [favorites, setFavorites] = useState([]);
  const [cart, setCart] = useState([]);

  const t = useCallback((k) => translations[lang][k] || k, [lang]);

  useEffect(() => {
    localStorage.setItem("appomax_lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("appomax_theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    if (user) {
      api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
    }
  }, [user]);

  const toggleLang = () => setLang((l) => (l === "es" ? "en" : "es"));
  const toggleTheme = () => setTheme((th) => (th === "light" ? "dark" : "light"));

  const toggleFavorite = async (productId) => {
    try {
      const { data } = await api.post(`/favorites/${productId}`);
      setFavorites(data.favorites);
    } catch (e) {
      toast.error("Error");
    }
  };

  const addToCart = (product, size, quantity) => {
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.size === size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        next[idx].subtotal = +(next[idx].quantity * next[idx].price).toFixed(2);
        return next;
      }
      const name = lang === "es" ? product.name_es : product.name_en;
      return [
        ...prev,
        {
          product_id: product.id,
          name,
          size,
          quantity,
          price: product.price,
          subtotal: +(quantity * product.price).toFixed(2),
          image: product.images?.[0] || "",
        },
      ];
    });
  };

  const updateCartQty = (index, delta) => {
    setCart((prev) => {
      const next = [...prev];
      const q = Math.max(1, next[index].quantity + delta);
      next[index] = { ...next[index], quantity: q, subtotal: +(q * next[index].price).toFixed(2) };
      return next;
    });
  };

  const removeFromCart = (index) => setCart((prev) => prev.filter((_, i) => i !== index));
  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((s, i) => s + i.subtotal, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <AppContext.Provider
      value={{
        lang, theme, t, toggleLang, toggleTheme,
        favorites, toggleFavorite,
        cart, addToCart, updateCartQty, removeFromCart, clearCart, cartTotal, cartCount,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
